import React, {
  type CSSProperties,
  type ForwardedRef,
  type Ref,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  Button,
  ConditionalWrapper,
  Icon,
  notEmpty,
  type Tooltip,
} from '../../module'
import { hasValue } from '../../module'
import FormLabel from '../FormLabel/FormLabel'
import styles from './_text-input.module.scss'

const ARROW_KEY_VALUES = ['arrowup', 'arrowdown']

type CalloutType = (stateName: StateNameType, value: string | number) => void
type StateNameType = string | number | undefined
type TooltipProps = React.ComponentProps<typeof Tooltip>
type TextInputBase = {
  /** The value to display in the input */
  value: string | number
  /** Optional callback when focus on input is lost */
  onBlurCallout?: (
    stateName: StateNameType,
    value: React.SyntheticEvent<HTMLTextAreaElement> | string,
  ) => void
  /** Optional number prop used to delay the execution used in a setTimeout's second param as milliseconds */
  debounce?: number
  /** Name of the state for the input */
  stateName?: string | number
  /** Optional callback function to specify when user releases a key on the keyboard */
  keyUp?: (stateName: StateNameType, value: unknown) => void
  /** Optional label above the input */
  labelText?: React.ReactNode
  /** Optional label to the top right of the input */
  rightLabel?: React.ReactNode
  /** Optional placeholder to display inside input */
  placeholder?: string
  /** Optional identifier for the input */
  id?: string
  /** Optional className for the input */
  classType?: string
  /** This gives the input the required attribute and will also display a red asterisk next to the label */
  required?: boolean
  /** Optional boolean to display the "Clear" text in the input */
  clear?: boolean
  /** Optional callback when the Clear button is clicked */
  clearCallout?: () => void
  /** Optional boolean to inidicate if the input should be initialized to the focused state */
  autoFocus?: boolean
  /** Optional show the browser's autocomplete popover */
  autoComplete?: 'on' | 'off'
  /** Optional label that appears inside the input and aligned to the right */
  endText?: string
  /** Boolean to know if an error has occured */
  hasError?: boolean
  /** Optional boolean to specify that an input field is read-only */
  readOnly?: boolean
  /** Optional boolean used to display full width of the input */
  fullWidth?: boolean
  /** Optional boolean to specify the maximum number of characters allowed in the input */
  maxLength?: number
  /** Optional function to specify when the input receives focus */
  setIsOnFocus?: (isOnFocus: boolean) => void
  /** Optional function to run when the input receives focus */
  onFocus?: () => void
  /** Optional function to run when pressing the return / enter key */
  onReturnCallout?: () => void
  /** Optional boolean to not allow the input to remain an empty field */
  noEmpty?: boolean
  /** The right aligned label in the input e.g. '%', 'in', 'lbs' etc */
  inputLabel?: string
  /** Optional class to add to the parent div of the input */
  containerClassName?: string
  /** Optional prop used to display a tooltip for the label  */
  labelTooltip?: Omit<TooltipProps, 'children'>
  /** Optional prop used to display an error message below the input  */
  errorText?: string
  /** Optional boolean to enable dynamic height for textarea */
  autoResize?: boolean
  /** Optional prop to add a test id to the TextInput for QA testing */
  qaTestId?: string
}

type TextInputNotNumber = TextInputBase & {
  /** Optional prop to indicate the type of the input */
  type?: 'email' | 'password' | 'tel' | 'text' | 'textarea'
  min?: never
  max?: never
  step?: never
  onlyWholeNumbers?: never
  onlyPositiveNumbers?: never
  noScrollForNumbers?: never
  noArrowKeyNumberChange?: never
  maxDecimalPlaces?: never
  numberFormat?: never
}

export type TextInputNumber = TextInputBase & {
  /** Required prop to indicate the input is of type `number` */
  type: 'number'
  /** Optional boolean to specify the minimum value for the input */
  min?: number
  /** Optional boolean to specify the maximum value for the input */
  max?: number
  /** Optional value to specify the interval between legal numbers in the input */
  step?: number
  /** Optional prop used to round a number to the nearest integer */
  onlyWholeNumbers?: boolean
  /** Optional prop used to only accept positive numbers as a value */
  onlyPositiveNumbers?: boolean
  /** Disable incrementing / decrementing number value with scroll wheel */
  noScrollForNumbers?: boolean
  /** Disable incrementing / decrementing number value with keyboard arrows (default: false) */
  noArrowKeyNumberChange?: boolean
  /** Used if we want to restrict a certain number of decimal places. */
  maxDecimalPlaces?: number
  /** Optional prop used to display currency or percentage input */
  numberFormat?: {
    /** To indicate the type of numberFormat */
    type: 'currency' | 'percentage'
    /** Currency symbol to display in a gray background (e.g: $) */
    currencySymbol?: string
  }
}

type TextInputTypes = TextInputNotNumber | TextInputNumber

// if disabled===true, then callout is optional
// if disabled===false or undefined, then callout is required
type DisabledOrNot =
  | { disabled: true; callout?: CalloutType }
  | { disabled?: false; callout: CalloutType }

export type TextInputProps = TextInputTypes & DisabledOrNot

const TextInput = ({
  value,
  type,
  onBlurCallout,
  debounce,
  callout,
  stateName,
  keyUp,
  labelText,
  rightLabel,
  placeholder,
  id,
  classType,
  required,
  clear,
  clearCallout,
  min,
  max,
  step,
  disabled,
  autoFocus,
  inputLabel,
  autoComplete,
  onlyWholeNumbers,
  onlyPositiveNumbers,
  endText,
  readOnly = false,
  fullWidth = false,
  noEmpty = false,
  maxLength,
  setIsOnFocus,
  onFocus,
  onReturnCallout,
  hasError = false,
  containerClassName = '',
  maxDecimalPlaces,
  labelTooltip,
  noScrollForNumbers,
  noArrowKeyNumberChange,
  numberFormat,
  autoResize,
  errorText,
  ref,
  qaTestId = 'text-input',
}: TextInputProps & {
  ref?: Ref<HTMLTextAreaElement | HTMLInputElement | null>
}): React.JSX.Element => {
  const [customClass, setCustomClass] = useState(''),
    classes = [customClass, classType].filter(Boolean).join(' '),
    [inputValue, setInputValue] = useState(value ? value : ''),
    timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined),
    [inputType, setInputType] = useState(type),
    inputLabelRef = useRef<HTMLSpanElement>(null),
    textAreaRef = useRef<HTMLTextAreaElement>(null)

  const onBlur = (
    value: React.SyntheticEvent<HTMLTextAreaElement> | string,
  ): void => {
    // TODO: This state name only uses "focus". It should be refactored to use a more descriptive name.
    // Clearing the focus class.
    setCustomClass('')
    if (onBlurCallout) {
      onBlurCallout(stateName ?? '', value)
    }
    if (setIsOnFocus) {
      setIsOnFocus(false)
    }
  }

  useEffect(() => {
    if (autoResize && type === 'textarea' && textAreaRef) {
      const textarea = textAreaRef?.current
      // if element is not present, return
      if (!textarea) return
      // function to adjust the height of the textarea
      const adjustHeight = () => {
        // Reset height to auto
        textarea.style.height = 'auto'
        // Set the height to the scrollHeight
        textarea.style.height = `${textarea.scrollHeight}px`
      }
      // Adjust height initially if there is pre-populated content
      adjustHeight()
      // Add event listener for input changes
      textarea?.addEventListener('input', adjustHeight)
      // Clean up event listener on component unmount
      return () => {
        // removing the event listener
        textarea?.removeEventListener('input', adjustHeight)
      }
    }
  }, [autoResize, type])

  const checkDebounce = (value: string | number) => {
    clearTimeout(timeoutRef.current)
    if (debounce) {
      timeoutRef.current = setTimeout(() => {
        callout?.(stateName, value)
      }, debounce)
    } else {
      callout?.(stateName, value)
    }
  }

  const setValue = (value: string): void => {
    let val: string | number = value
    if (inputType === 'number') {
      val = onlyWholeNumbers
        ? val
          ? Math.round(val as unknown as number)
          : ''
        : val
      val = onlyPositiveNumbers && (val as unknown as number) < 0 ? 0 : val
      if (
        hasValue(min) &&
        (val as unknown as number) < (min as number) &&
        value !== ''
      )
        val = min as number
      if (
        hasValue(max) &&
        (val as unknown as number) > (max as number) &&
        value !== ''
      )
        val = max as number

      if (value === '' && noEmpty) {
        val = min ?? 0
      }
      if (maxDecimalPlaces) {
        const arr = value.split('.')
        if (arr.length > 1 && arr[1].length > maxDecimalPlaces) return
      }
    }
    setInputValue(val)
    checkDebounce(val)
  }

  const clearValue = () => {
    setInputValue('')
    if (clearCallout) {
      clearCallout()
    }
    // Focus the input after the clear button is clicked
    setTimeout(() => {
      if (ref && 'current' in ref) {
        ref.current?.focus()
      }
    }, 100)
  }

  const onInputFocus = () => {
    setCustomClass('focus')
    setIsOnFocus?.(true)
    onFocus?.()
  }

  const togglePassword = () => {
    setInputType(inputType === 'password' ? 'text' : 'password')
  }

  const handleKeyUp = (event: React.KeyboardEvent) => {
    switch (event.keyCode) {
      case 13:
        keyUp && keyUp(stateName, inputValue)
        break
      case 27:
        if (inputValue && (inputValue as string).length > 0) {
          clearValue()
        } else if (!inputValue || (inputValue as string).length === 0) {
          const target = event.target as HTMLInputElement | HTMLTextAreaElement
          target.blur()
        }
        break
      default:
        break
    }
  }

  useEffect(
    () => () => {
      timeoutRef.current && clearTimeout(timeoutRef.current)
    },
    [],
  )

  useEffect(() => {
    setInputType(type)
  }, [type])

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const inputRefOffsetWidth = inputLabelRef?.current?.offsetWidth
  const inlineStyles = {
    '--input-label-width': (inputRefOffsetWidth ?? 0) + 12 + 'px',
  } as CSSProperties

  return (
    <div
      data-testid='text-input-container'
      className={`${styles.textInput} ${
        fullWidth ? styles.fullWidth : ''
      } ${containerClassName}`}
      qa-test-id={qaTestId}
    >
      {labelText && (
        <div className={labelTooltip && `${styles.labelText}`}>
          <FormLabel
            label={labelText}
            rightLabel={rightLabel}
            tooltip={labelTooltip}
            required={required}
            error={hasError}
            id={id}
            disabled={disabled}
          />
        </div>
      )}

      {type === 'textarea' ? (
        <textarea
          data-testid='text-input-textarea'
          id={id}
          // for autoResize, use textAreaRef, else use ForwardedRef
          ref={
            autoResize
              ? textAreaRef
              : (ref as ForwardedRef<HTMLTextAreaElement>)
          }
          value={inputValue}
          onBlur={onBlur}
          onFocus={onInputFocus}
          placeholder={placeholder}
          required={required}
          className={
            classes + ` ${autoResize ? styles.textareaAutoResize : ''}`
          }
          onKeyUp={(e) => handleKeyUp(e)}
          onChange={(e) => setValue(e.target.value)}
          disabled={disabled}
          autoFocus={autoFocus}
          readOnly={readOnly}
          maxLength={maxLength}
        />
      ) : (
        <>
          <div
            data-testid='text-input-wrapper'
            className={`${styles.inputContainer} ${
              inputLabel &&
              inputLabelRef?.current &&
              numberFormat?.type !== 'percentage'
                ? styles.hasLabel
                : ''
            }${fullWidth ? styles.fullWidth : ''} ${
              numberFormat?.type === 'currency' ? styles.flex : ''
            }`}
            style={inlineStyles}
          >
            {numberFormat?.type === 'currency' && (
              <span
                data-testid='text-input-currency'
                className={`${styles.inputPrefix} ${
                  customClass === 'error' || classType === 'error'
                    ? styles.prefixError
                    : ''
                } ${customClass === 'focus' ? styles.prefixActive : ''}`}
              >
                {numberFormat?.currencySymbol}
              </span>
            )}

            {type === 'password' && inputValue !== '' && (
              <Button as='unstyled' onClick={togglePassword}>
                <Icon
                  icon={
                    inputType === 'password' ? 'eyeglassesNo' : 'eyeglasses'
                  }
                  iconSize='20px'
                  className={styles.iconStyle}
                />
              </Button>
            )}
            <ConditionalWrapper
              condition={Boolean(endText)}
              wrapper={(children) => (
                <div className={styles.conditionalWrapperFlex}>{children}</div>
              )}
            >
              <>
                <input
                  data-testid='text-input-field'
                  id={id}
                  ref={ref as ForwardedRef<HTMLInputElement>}
                  type={inputType ? inputType : 'text'}
                  value={inputValue}
                  onBlur={(e) => onBlur(e.target.value)}
                  onFocus={onInputFocus}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    ;(e.target as HTMLInputElement).focus()
                  }}
                  onTouchEnd={(e) => {
                    e.stopPropagation()
                    ;(e.target as HTMLInputElement).focus()
                  }}
                  placeholder={placeholder}
                  required={required}
                  className={`${classes} ${endText ? styles.endInput : ''} ${
                    numberFormat?.type === 'currency'
                      ? styles.currencyInput
                      : ''
                  }`}
                  onKeyUp={(e) => handleKeyUp(e)}
                  onChange={(e) => setValue(e.target.value)}
                  min={min}
                  max={max}
                  onWheel={(e) => noScrollForNumbers && e.currentTarget.blur()}
                  maxLength={maxLength}
                  disabled={disabled}
                  autoFocus={autoFocus}
                  autoComplete={autoComplete || 'on'}
                  readOnly={readOnly}
                  step={step}
                  onKeyDown={(e) => {
                    if (
                      noArrowKeyNumberChange &&
                      inputType === 'number' &&
                      ARROW_KEY_VALUES.includes(e.key.toLowerCase())
                    ) {
                      e.preventDefault()
                    }
                    if (e.key === 'Enter') {
                      onReturnCallout?.()
                    }
                  }}
                />
                {endText && (
                  <input
                    data-testid='text-input-end-text'
                    type='text'
                    className={`${styles.endText} ${classes}`}
                    value={endText}
                    disabled
                  />
                )}
                {inputLabel ? (
                  <span
                    data-testid='text-input-suffix'
                    className={`${styles.inputSuffix} ${customClass === 'error' ? styles.prefixError : ''} ${
                      customClass === 'focus' ? styles.prefixActive : ''
                    }`}
                    ref={inputLabelRef}
                  >
                    {inputLabel}
                  </span>
                ) : null}
                {clear && !disabled && notEmpty(value) && (
                  <Button
                    as='unstyled'
                    className={styles.buttonForXIcon}
                    onClick={clearValue}
                  >
                    <Icon
                      icon='x'
                      iconSize='12px'
                      className={`${styles.xIcon} ${
                        customClass === 'focus' ? styles.focused : ''
                      }`}
                    />
                  </Button>
                )}
              </>
            </ConditionalWrapper>
          </div>
        </>
      )}
      {(customClass === 'error' || classType?.includes('error')) &&
        errorText && (
          <div data-testid='text-input-error' className={styles.errorTextStyle}>
            {errorText}
          </div>
        )}
    </div>
  )
}

TextInput.displayName = 'TextInput'
export default TextInput
